import React, { useState, useEffect } from 'react'
import gql from 'graphql-tag'
import moment from 'moment'
import compose from 'lodash/flowRight'
import * as enums from '../../../lib/enums'
import { Button } from '../../../components/Button'
import {
    Container,
    ColContainer,
    TPL_THIRDS,
    CardContainer,
} from '../../../components/Container'
import { DateRangeInput } from '../../../components/DateRangeInput'
import { H1, H2 } from '../../../components/Typography'
import { Helmet } from 'react-helmet'
import { LoadingError } from '../../../components/LoadingFork'
import { SelectInput } from '../../../components/SelectInput'
import { css } from 'glamor'
import { stringify } from 'qs'
import { withInstallation } from '../../../components/InstallationContext'
import {
    isAfter,
    addDays,
    addMonths,
    addYears,
    setMonth,
    startOfDay,
    endOfDay,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
} from 'date-fns'
import { DateTime } from 'luxon'
import drivoApollo, {DRIVO_URL} from "../../../lib/drivoApollo";
import {downloadWithAuth} from "../../../lib/authManager";
import api from '../../../lib/api'

const ALL_USERS_QUERY = gql`
  query allUsers {
    allUsers {
      id
      name
    }
  }
`;

function Bookings({ installation }) {
    const TODAY = DateTime.now().setZone(installation.timeZoneName).toJSDate()
    const YESTERDAY = addDays(TODAY, -1)
    const LAST_MONTH = addMonths(TODAY, -1)
    const NEXT_YEAR = addYears(TODAY, 1)
    const LAST_YEAR = addYears(TODAY, -1)

    const DATE_RANGE_OPTIONS = {
        Today: [startOfDay(TODAY), endOfDay(TODAY)],
        Yesterday: [startOfDay(YESTERDAY), endOfDay(YESTERDAY)],
        'This month': [startOfMonth(TODAY), endOfMonth(TODAY)],
        'Last month': [startOfMonth(LAST_MONTH), endOfMonth(LAST_MONTH)],
        YTD: [startOfYear(TODAY), endOfYear(TODAY)],
        'FY YTD':
          isAfter(TODAY, startOfMonth(setMonth(TODAY, 6)))
            ? [startOfMonth(setMonth(TODAY, 6)), endOfMonth(setMonth(NEXT_YEAR, 5))]
            : [startOfMonth(setMonth(LAST_YEAR, 6)), endOfMonth(setMonth(TODAY, 5))],
    };

    const [dates, setDates] = useState(DATE_RANGE_OPTIONS.Today)
    const [status, setStatus] = useState('')
    const [employee, setEmployee] = useState('')

    const [users, setUsers] = useState([])
    const [dataCount, setDataCount] = useState(0)

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const [startDate, endDate] = dates

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setError(null)
                const { data } = await drivoApollo.query({
                    query: ALL_USERS_QUERY,
                    fetchPolicy: 'network-only',
                })

                setUsers(data?.allUsers || [])
            } catch (e) {
                setError(e)
            }
        }

        if (installation?.id) {
            fetchUsers()
        }
    }, [installation?.id])

    useEffect(() => {
        if (!installation?.id) return;

        const controller = new AbortController();

        const fetchPreview = async () => {
            try {
                setLoading(true);
                setError(null);

                const start = DateTime.fromJSDate(
                    startDate || DateTime.now().toJSDate(),
                    { zone: installation.timeZoneName }
                ).toISODate();

                const end = DateTime.fromJSDate(
                    endDate || DateTime.now().toJSDate(),
                    { zone: installation.timeZoneName }
                ).toISODate();

                const params = new URLSearchParams({ start, end });

                if (status) params.append('status', status);
                if (employee) params.append('employee', employee);

                const { data } = await api.bookings(installation.id, params, { signal: controller.signal });

                setDataCount(data.count || 0);
            } catch (e) {
                if (e.name === 'CanceledError') return;
                console.error(e.message);
                setError(e);
            } finally {
                setLoading(false);
            }
        };

        fetchPreview();

        return () => controller.abort();
    }, [installation?.id, startDate, endDate, status, employee]);

    const employeeOptions = [
        { label: 'All employees', value: '' },
        ...users.map(({ id, name }) => ({
            label: name,
            value: id,
        })),
    ]

    const hasData = dataCount > 0
    const datesReady = startDate && endDate
    const downloadReady = datesReady && hasData

    const handleDownload = async (e) => {
        e.preventDefault();

        if (!downloadReady) return;

        const startISO = DateTime.fromJSDate(startDate, { zone: installation.timeZoneName }).toISODate();
        const endISO = DateTime.fromJSDate(endDate, { zone: installation.timeZoneName }).toISODate();

        const query = {
            start: startISO,
            end: endISO,
        }
        if (status) query.status = status;
        if (employee) query.employee = employee;
        const downloadURL = `${DRIVO_URL}/bookings/${installation.id}/download?${stringify(query)}`;
        const fileName = `${startISO}-${endISO}-${installation.brandingKey}-bookings.xlsx`;

        await downloadWithAuth({ url: downloadURL, filename: fileName });
    };

    return (
        <div>
            <CardContainer>
                <Helmet>
                    <title>Bookings</title>
                </Helmet>

                <Container size="large" spaceAbove="small" spaceBelow="none">
                    <div
                        className={css({
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        })}
                    >
                        <H1>Bookings Data Export</H1>
                    </div>
                </Container>

                <Container size="large" spaceAbove="small" spaceBelow="medium">
                    <H2>Filters</H2>

                    {error && <LoadingError error={error} />}

                    <ColContainer columnTemplate={TPL_THIRDS} spaceAbove="medium">
                        <DateRangeInput
                            float
                            label="Date Range"
                            options={DATE_RANGE_OPTIONS}
                            onChange={(dates) => setDates(dates.map((x) => (x.toDate ? x.toDate() : x)))}
                            value={dates.map((x) => moment(x))}
                        />

                        <SelectInput
                            label="Booking Status"
                            autoComplete="off"
                            options={enums.BOOKING_STATUS_SEARCH_OPTIONS}
                            onChange={(v) => setStatus(v.target.value)}
                            value={status}
                        />

                        <SelectInput
                            label="Employee"
                            autoComplete="off"
                            options={employeeOptions}
                            onChange={(v) => setEmployee(v.target.value)}
                            value={employee}
                        />
                    </ColContainer>

                    <div>
                        {loading
                            ? 'Loading...'
                            : hasData
                                ? `${dataCount} bookings found`
                                : 'No bookings found'}
                    </div>

                    <Button
                        spaceAbove="small"
                        onClick={handleDownload}
                        disabled={!downloadReady}
                    >
                        Download
                    </Button>
                </Container>
            </CardContainer>
        </div>
    )
}

export default compose(withInstallation)(Bookings)